import React, { useCallback, useEffect, useRef, useState } from "react";
import LpField from "../../../components/common/linchpin/LpField";
import LpInput from "../../../components/common/linchpin/LpInput";
import LpAddressSearch from "../../../components/common/linchpin/LpAddressSearch";
import { Search } from "../../../components/common/linchpin/LpIcons";
import MobileInput_v2 from "../../../components/common/ReusableMobileInput";

// Same data + behavior as the legacy cross-dock form that lived inline in
// AddressLocationDetails.rendeCrossDockOrderForm — only the visual primitives
// changed (Form.Item -> LpField, ReusableInput -> LpInput, SearchAddress_v2
// -> LpAddressSearch, MobileInput_v2 in linchpin mode) so the card matches
// the regular leg (AddressV2). No new fields; no header adds.
//
// Validation flows through the parent's location state via onChangeDirect
// (the legacy Form.Items had no rules= props and formRef was unused — real
// validation runs in AddressLocationDetails' validate* blocks against the
// state these handlers update).
function CrossDockFormV2({
  location = {},
  onChangeDirect,
  onSelect,
  orderTypeConfig,
  selectedWarehouseId,
  disabled = false,
}) {
  // Parent's handlers get fresh identity per render; stash in refs so the
  // memoized LpInput / LpAddressSearch children don't re-render on every
  // parent re-render. Matches the AddressV2 pattern (see notes there).
  const onChangeDirectRef = useRef(onChangeDirect);
  const onSelectRef = useRef(onSelect);
  useEffect(() => { onChangeDirectRef.current = onChangeDirect; });
  useEffect(() => { onSelectRef.current = onSelect; });

  const onCompanyChange = useCallback(
    (e) => onChangeDirectRef.current("company_name", e.target.value),
    []
  );
  const onEmailChange = useCallback(
    (e) => onChangeDirectRef.current("customer_email", e.target.value),
    []
  );
  const onPhone1Change = useCallback(
    (val) => onChangeDirectRef.current("customer_phone_one", val),
    []
  );
  // LpAddressSearch is a controlled input — without local state the value
  // stayed pinned to "" so typed characters never appeared. Mirror AddressV2:
  // own the search text locally, clear it after a successful pick.
  const [searchText, setSearchText] = useState("");
  const onSearchSelect = useCallback((item) => {
    onSelectRef.current(item);
    setSearchText("");
  }, []);

  return (
    <div className="lp-v3 lav2-root">
      <LpField label="Search Location">
        <LpAddressSearch
          value={searchText}
          onChange={setSearchText}
          onSelect={onSearchSelect}
          mode="both"
          placeholder="Search name of company, location, or address"
          prefix={<Search width={16} height={16} />}
          orderTypeConfig={orderTypeConfig}
          selectedWarehouseId={selectedWarehouseId}
          disabled={disabled}
          name="cdf2_search"
        />
      </LpField>

      <LpField label="Company">
        <LpInput
          data-testid="company-name-input"
          value={location.company_name || ""}
          onChange={onCompanyChange}
          placeholder="Company name"
          disabled={disabled}
          name="cdf2_company_name"
          disableBrowserAutofill
        />
      </LpField>

      <div className="lav2-row">
        <div className="lav2-col-2" data-testid="customer-phone-1-input">
          <LpField label="Phone 1">
            <MobileInput_v2
              value={location.customer_phone_one}
              handleInputChange={onPhone1Change}
              disabled={disabled}
              linchpin
              wrapperClassName="lav2-phone"
              placeholder="Phone number"
              name="cdf2_customer_phone_one"
              disableBrowserAutofill
            />
          </LpField>
        </div>
        <div className="lav2-col-2">
          <LpField label="Email">
            <LpInput
              data-testid="customer-email-input"
              type="email"
              value={location.customer_email || ""}
              onChange={onEmailChange}
              placeholder="Email Address"
              disabled={disabled}
              name="cdf2_customer_email"
              disableBrowserAutofill
            />
          </LpField>
        </div>
      </div>
    </div>
  );
}

export default React.memo(CrossDockFormV2);
